import { useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../services/firebase';
import { isAllowedAdmin } from '../config/auth';

/**
 * Auth hook that supports both admin and teacher roles.
 *
 * - Admin: email in ALLOWED_ADMINS list → sees full admin dashboard
 * - Teacher: email in Firestore teachers collection → sees teacher portal only
 * - Neither: access denied
 */
export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [role, setRole] = useState(null); // 'admin' | 'teacher' | null
  // v2 cutover (Phase F): teacherProfile holds the doc so consumers can read
  // use_v2_dashboard (and teacher_id, used for the v2 default-landing URL).
  const [teacherProfile, setTeacherProfile] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const adminStatus = isAllowedAdmin(user.email);

        if (adminStatus) {
          setUser(user);
          setRole('admin');
          setTeacherProfile(null);
          setError(null);
        } else {
          // Check if user is a teacher by calling the /me endpoint
          try {
            const token = await user.getIdToken();
            const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
            const response = await fetch(`${API_URL}/api/teachers/me`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
              const json = await response.json().catch(() => ({}));
              setUser(user);
              setRole('teacher');
              setTeacherProfile(json?.data || null);
              setError(null);
            } else {
              setUser(user);
              setRole(null);
              setTeacherProfile(null);
              setError('Access denied. You are not authorized as an admin or teacher.');
            }
          } catch {
            setUser(user);
            setRole(null);
            setTeacherProfile(null);
            setError('Access denied. You are not authorized as an admin or teacher.');
          }
        }
      } else {
        setUser(null);
        setRole(null);
        setTeacherProfile(null);
        setError(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    try {
      setError(null);
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check admin first
      if (isAllowedAdmin(user.email)) {
        return user;
      }

      // Check teacher
      try {
        const token = await user.getIdToken();
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
        const response = await fetch(`${API_URL}/api/teachers/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          return user;
        }
      } catch {
        // Not a teacher either
      }

      // Neither admin nor teacher
      await signOut(auth);
      throw new Error('Access denied. You are not authorized as an admin or teacher.');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    user,
    loading,
    error,
    role,
    isAdmin: role === 'admin',
    isTeacher: role === 'teacher',
    isAuthenticated: role !== null,
    teacherProfile,
    teacherId: teacherProfile?.id || teacherProfile?.teacher_id || null,
    signInWithGoogle,
    logout
  };
}

export default useAuth;
